import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import { getCvssColorFromScore } from '@totem/components/software/cves/utilities';
import ReviewActions from '@totem/components/software/vulnerabilityReport/reviewActions';
import { SoftwareVulnerabilityReportRecord } from '@totem/components/software/vulnerabilityReport/types';
import VulnerabilityReportContext from '@totem/components/software/vulnerabilityReport/vulnerabilityReportContext';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import VendorDisplay from '@totem/components/vendors/vendorLogos/vendorDisplay';
import { isNotNull } from '@totem/utilities/common';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';
const VulnerabilityReportTable = () => {
  const { data, loading, input, filterOptions, setInput } = useContext(
    VulnerabilityReportContext,
  );
  const { regions, buildings } = useContext(OrganizationContext);
  const { userProfile } = useContext(UserProfileContext);
  const canReview =
    permissions.internalSystemAdministrator.hasPermissions(userProfile);
  const totalRecords =
    isNotNull(data) && isNotNull(data.paging) ? data.paging.totalRecords : 0;

  const getData = () => {
    if (isNotNull(data) && isNotNull(data.data)) {
      return data.data;
    }
    return [];
  };

  const reasonText: Record<string, string> = {
    'version-in-range': "The installed version is in the CVE's affected range.",
    manual: 'Set by an administrator.',
    'no-range-data':
      'The CVE does not state which versions are affected. Review and mark it affected or not affected.',
    'version-not-comparable':
      "The installed version cannot be compared with the CVE's affected versions. Review and mark it affected or not affected.",
  };

  // The newest CVSS version with a score: 4.0, then 3.x, then 2.
  const getBestScore = (rec: SoftwareVulnerabilityReportRecord) => {
    const candidates = [
      {
        label: 'CVSS V4',
        score: rec.cveV4BaseScore,
        severity: rec.cveV4BaseSeverity,
      },
      {
        label: 'CVSS V3',
        score: rec.cveV3BaseScore,
        severity: rec.cveV3BaseSeverity,
      },
      {
        label: 'CVSS V2',
        score: rec.cveV2BaseScore,
        severity: rec.cveV2BaseSeverity,
      },
    ];

    return (
      candidates.find(
        (candidate) =>
          typeof candidate.score === 'number' && candidate.score > 0,
      ) ?? null
    );
  };

  const getCvss = (rec: SoftwareVulnerabilityReportRecord) =>
    getBestScore(rec)?.label ?? '';

  const getCvssBaseScore = (rec: SoftwareVulnerabilityReportRecord) =>
    getBestScore(rec)?.score ?? '';

  const getCvssBaseScoreNumeric = (rec: SoftwareVulnerabilityReportRecord) =>
    getBestScore(rec)?.score ?? 0;

  const getCvssBaseSeverity = (rec: SoftwareVulnerabilityReportRecord) =>
    getBestScore(rec)?.severity ?? '';

  const columns: ColumnProps<SoftwareVulnerabilityReportRecord>[] = [
    {
      title: 'Software Package',
      key: 'packageName',
      dataIndex: 'packageName',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return (
          <Link to={`/dashboard/software/${record.packageId}`}>
            {record.packageName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.packageName, compB.packageName),
      filterMultiple: true,
      filteredValue: stringifyArray(input.packageName),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.packageName)
          ? filterOptions.packageName
              .sort((compA, compB) =>
                sortStringAscending(compA.name, compB.name),
              )
              .map((chk) => {
                return { text: chk.name, value: chk.value };
              })
          : [],
    },
    {
      title: 'Version',
      dataIndex: 'packageVersion',
      key: 'packageVersion',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return record.packageVersion;
      },
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.packageVersion, compB.packageVersion),
      filterMultiple: true,
      filteredValue: stringifyArray(input.packageVersion),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.packageVersion)
          ? filterOptions.packageVersion
              .sort((compA, compB) =>
                sortStringAscending(compA.name, compB.name),
              )
              .map((chk) => {
                return { text: chk.name, value: chk.value };
              })
          : [],
    },
    {
      title: 'Publisher',
      dataIndex: 'packagePublisher',
      key: 'packagePublisher',
      render: (_, record: SoftwareVulnerabilityReportRecord) => (
        <VendorDisplay vendorName={record.packagePublisher} />
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.packagePublisher, compB.packagePublisher),
      filterMultiple: true,
      filteredValue: stringifyArray(input.packagePublisher),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.packagePublisher)
          ? filterOptions.packagePublisher
              .sort((compA, compB) =>
                sortStringAscending(compA.name, compB.name),
              )
              .map((chk) => {
                return { text: chk.name, value: chk.value };
              })
          : [],
    },
    {
      title: 'Tags',
      dataIndex: 'packageTags',
      key: 'tags',
      render: (_, record: SoftwareVulnerabilityReportRecord) =>
        (record.packageTags ?? []).map((tag) => <Tag key={tag}>{tag}</Tag>),
      filterMultiple: true,
      filterSearch: true,
      filteredValue: stringifyArray(input.tags),
      filters: (filterOptions?.tags ?? []).map((option) => ({
        text: option.name,
        value: option.value,
      })),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record: SoftwareVulnerabilityReportRecord) => (
        <Tooltip title={reasonText[record.reason] ?? record.reason}>
          {record.status === 'review' ? (
            <Tag color="gold">Needs review</Tag>
          ) : (
            <Tag color="red">Affected</Tag>
          )}
        </Tooltip>
      ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.status),
      filters: [
        { text: 'Affected', value: 'affected' },
        { text: 'Needs review', value: 'review' },
      ],
    },
    {
      title: 'Vulnerability',
      dataIndex: 'vulnerability',
      key: 'vulnerability',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return (
          <Link to={`/dashboard/software/cve/${record.vulnerability}`}>
            {record.vulnerability}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.vulnerability, compB.vulnerability),
      filterMultiple: true,
      filteredValue: stringifyArray(input.vulnerability),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.cve)
          ? filterOptions.cve
              .sort((compA, compB) =>
                sortStringAscending(compA.name, compB.name),
              )
              .map((chk) => {
                return { text: chk.name, value: chk.value };
              })
          : [],
    },
    {
      title: 'CVSS',
      dataIndex: 'cvss',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return getCvss(record);
      },
    },
    {
      title: 'Base Score',
      dataIndex: 'score',
      sortDirections: ['descend', 'ascend'],
      sorter: true,
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return (
          <span
            style={{
              color: getCvssColorFromScore(getCvssBaseScoreNumeric(record)),
            }}
          >
            {getCvssBaseScore(record)}
          </span>
        );
      },
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      filterMultiple: true,
      filteredValue: stringifyArray(input.severity),
      filters: [
        { text: 'Critical', value: 'CRITICAL' },
        { text: 'High', value: 'HIGH' },
        { text: 'Medium', value: 'MEDIUM' },
        { text: 'Low', value: 'LOW' },
        { text: 'Not scored', value: 'NONE' },
      ],
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return (
          <span
            style={{
              color: getCvssColorFromScore(getCvssBaseScoreNumeric(record)),
            }}
          >
            {getCvssBaseSeverity(record)}
          </span>
        );
      },
    },
    {
      title: 'Known Exploited',
      dataIndex: 'knownExploited',
      key: 'knownExploited',
      sortDirections: ['descend', 'ascend'],
      sorter: true,
      filterMultiple: false,
      filteredValue: stringifyArray(input.knownExploited),
      filters: [
        { text: 'Known exploited', value: 'true' },
        { text: 'Not known exploited', value: 'false' },
      ],
      render: (_, record: SoftwareVulnerabilityReportRecord) =>
        record.knownExploited ? (
          <Tooltip
            title={`In CISA's Known Exploited Vulnerabilities catalog since ${record.kevDateAdded}${
              record.kevRansomware === 'Known'
                ? '; used in ransomware campaigns'
                : ''
            }. Federal remediation due ${record.kevDueDate}.`}
          >
            <Tag color="red">Exploited</Tag>
          </Tooltip>
        ) : null,
    },
    {
      title: 'EPSS',
      dataIndex: 'epssScore',
      sortDirections: ['descend', 'ascend'],
      sorter: true,
      render: (_, record: SoftwareVulnerabilityReportRecord) =>
        typeof record.epssScore === 'number' && record.epssScore > 0 ? (
          <Tooltip
            title={`${(record.epssScore * 100).toFixed(1)}% probability of exploitation in the next 30 days (higher than ${(
              (record.epssPercentile ?? 0) * 100
            ).toFixed(0)}% of CVEs).`}
          >
            {(record.epssScore * 100).toFixed(1)}%
          </Tooltip>
        ) : null,
    },
    {
      title: 'Fixed In',
      dataIndex: 'fixedVersion',
      render: (_, record: SoftwareVulnerabilityReportRecord) =>
        record.fixedVersion ?? '',
    },
    {
      title: 'Region',
      dataIndex: 'regionName',
      key: 'regionId',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return record.regionName;
      },
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.regionName, compB.regionName),
      filterMultiple: true,
      filteredValue: stringifyArray(input.regionId),
      filters:
        typeof regions !== 'undefined' && regions !== null
          ? regions.map((region) => {
              return { text: region.name, value: region.id };
            })
          : [],
    },
    {
      title: 'Building',
      dataIndex: 'buildingName',
      key: 'buildingId',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return (
          <Link to={`/dashboard/buildings/${record.buildingId}`}>
            {record.buildingName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.buildingName, compB.buildingName),
      filterMultiple: true,
      filteredValue: stringifyArray(input.buildingId),
      filters:
        typeof buildings !== 'undefined' && buildings !== null
          ? buildings.map((building) => {
              return { text: building.name, value: building.id };
            })
          : [],
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystemName',
      key: 'controlSystemId',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return (
          <Link to={`/dashboard/controlsystems/${record.controlSystemId}`}>
            {record.controlSystemName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.controlSystemName, compB.controlSystemName),
      filterMultiple: true,
      filterSearch: true,
      filteredValue: stringifyArray(input.controlSystemId),
      filters: (filterOptions?.controlSystem ?? []).map((option) => ({
        text: option.name,
        value: option.value,
      })),
    },
    {
      title: 'Device Name',
      dataIndex: 'deviceName',
      key: 'deviceId',
      render: (_, record: SoftwareVulnerabilityReportRecord) => {
        return (
          <Link to={`/dashboard/devices/${record.deviceId}`}>
            {record.deviceName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.deviceName, compB.deviceName),
      filterMultiple: true,
      filterSearch: true,
      filteredValue: stringifyArray(input.deviceId),
      filters: (filterOptions?.device ?? []).map((option) => ({
        text: option.name,
        value: option.value,
      })),
    },
  ];

  if (canReview) {
    columns.push({
      title: 'Review',
      key: 'review',
      render: (_, record: SoftwareVulnerabilityReportRecord) =>
        record.status === 'review' ? <ReviewActions record={record} /> : null,
    });
  }

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<SoftwareVulnerabilityReportRecord>,
    sorter,
  ) => {
    const { ...params } = filters;

    const sortDir: string = sorter.order === 'descend' ? '-1' : '1';
    let sortField = 'packageName';
    if (
      typeof sorter.field !== 'undefined' &&
      typeof sorter.order !== 'undefined' &&
      sorter.field !== ''
    ) {
      sortField = sorter.field;
    }

    // @ts-ignore
    setInput({
      ...input,
      ...params,
      pageSize: updatedPagination.pageSize,
      page: updatedPagination.current,
      sortField,
      sortDirection: sortDir,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getData()}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.page,
        pageSize: input.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) =>
        record.deviceId +
        record.packageId +
        record.packageVersion +
        record.vulnerability
      }
    />
  );
};

export default VulnerabilityReportTable;
